import React from "react";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import { ColumnsManagement } from "./filters/ColumnsManagement";
import { GridColDef, GridColumnVisibilityModel } from "@mui/x-data-grid";
import { AdvancedViewFilters } from "./filters/AdvancedViewFilters";
import { Filter, GlobalSearchSelectOption } from "data/@types/GlobalSearchData";
import { styled, Theme } from "@mui/material/styles";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const StyledTab = styled(Tab)(({ theme }: { theme: Theme }) => ({
  "&&": {
    border: 0,
    px: 0,
    writingMode: "vertical-rl",
    color: theme.palette.text.secondary,
    fontWeight: 400,
    fontSize: "14px",
    textTransform: "uppercase",
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },
  "&&.Mui-selected": {
    color: theme.palette.common.caBlue?.lighter,
  },
}));

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`vertical-tabpanel-${index}`}
      aria-labelledby={`vertical-tab-${index}`}
      style={{
        padding: 0,
        overflowY: "auto",
        width: "100%",
      }}
      {...other}
    >
      {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `vertical-tab-${index}`,
    "aria-controls": `vertical-tabpanel-${index}`,
  };
}

type GlobalSearchTabsProps = {
  filters: Filter[];
  columnsVisibility: GridColumnVisibilityModel;
  onColumnVisibilityChange: (columnName: string, isVisible: boolean) => void;
  columns: GridColDef[];
  activityType: GlobalSearchSelectOption;
};

export const GlobalSearchTabs = ({
  filters,
  columnsVisibility,
  onColumnVisibilityChange,
  columns,
  activityType,
}: GlobalSearchTabsProps) => {
  const [value, setValue] = React.useState(0);

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  return (
    <Box
      sx={{
        bgcolor: "background.paper",
        mt: 5,
        mr: 2,
        ml: 1,
        display: "flex",
        height: "100%",
        width: "20%",
      }}
    >
      <TabPanel value={value} index={0}>
        <AdvancedViewFilters
          columnsVisibility={columnsVisibility}
          filters={filters}
          activityType={activityType}
        />
      </TabPanel>
      <TabPanel value={value} index={1}>
        <ColumnsManagement
          columns={columns}
          columnsVisibility={columnsVisibility}
          onChange={onColumnVisibilityChange}
        />
      </TabPanel>
      <Tabs
        orientation="vertical"
        variant="standard"
        scrollButtons={false}
        value={value}
        onChange={handleChange}
        aria-label="Filters and Columns tabs"
        sx={{
          borderLeft: 1,
          borderColor: "divider",
          "& .MuiTab-root": {
            minWidth: "50px",
            width: "50px",
          },
        }}
      >
        <StyledTab label="Filters" {...a11yProps(0)} />
        <StyledTab label="Columns" {...a11yProps(1)} />
      </Tabs>
    </Box>
  );
};
